import React, { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { Grid, Box, Typography, Button, Paper } from '@mui/material'
import { Campaign as CampaignIcon, Add as AddIcon } from '@mui/icons-material'

import CampaignCard from './CampaignCard'
import ConfirmationModal from '../../mailTemplate/src/components/atomic/molecules/ConfirmationModal'
import { deleteCampaign } from '../../mailTemplate/src/services/B2BServices'
import { useToast } from '../hooks/useToast'

const CampaignCardList = ({
  data,
  fetchCampaigns,
  campaignReports,
  refreshCampaignReport,
  campaignReportsFetching,
  onCreateCampaign
}) => {
  const { t } = useTranslation()
  const { showError } = useToast()

  const [confirmationModalItem, setConfirmationModalItem] = useState(null)
  const [confirmationModalLoading, setConfirmationModalLoading] = useState(false)

  const handleCampaignDelete = useCallback(async () => {
    setConfirmationModalLoading(true)

    try {
      await deleteCampaign(confirmationModalItem.id)
      setConfirmationModalItem(null)
      fetchCampaigns()
    } catch (err) {
      showError(err || t('mailTemplate.campaigns.deleteModal.toastErr'))
    } finally {
      setConfirmationModalLoading(false)
    }
  }, [confirmationModalItem, fetchCampaigns, showError, t])

  const closeConfirmationModal = useCallback(() => {
    setConfirmationModalItem(null)
  }, [])

  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          px: 2
        }}
      >
        <Paper
          elevation={0}
          sx={{
            textAlign: 'center',
            p: 8,
            maxWidth: 600,
            width: '100%',
            backgroundColor: 'grey.50',
            border: '2px dashed',
            borderColor: 'grey.300',
            borderRadius: 3
          }}
        >
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            <CampaignIcon
              sx={{
                fontSize: 80,
                color: 'primary.light',
                opacity: 0.7
              }}
            />
          </Box>
          <Typography
            variant="h5"
            component="h2"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: 'text.primary'
            }}
          >
            {t('mailTemplate.campaigns.noCampaigns')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              lineHeight: 1.6
            }}
          >
            {t('mailTemplate.campaigns.createFirstCampaign')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={onCreateCampaign}
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1.5
            }}
          >
            {t('mailTemplate.campaigns.createCampaign')}
          </Button>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Grid container spacing={2}>
        {data.map((item, index) => (
          <Grid item key={item.id || index} xs={12} sm={6} lg={4} xl={3}>
            <CampaignCard
              campaignItem={item}
              setConfirmationModalItem={setConfirmationModalItem}
              campaignReport={campaignReports?.[item.id]}
              refreshCampaignReport={refreshCampaignReport}
              campaignReportFetching={campaignReportsFetching?.[item.id]}
            />
          </Grid>
        ))}
      </Grid>

      <ConfirmationModal
        open={Boolean(confirmationModalItem)}
        close={closeConfirmationModal}
        loading={confirmationModalLoading}
        submit={handleCampaignDelete}
        title={t('mailTemplate.campaigns.deleteModal.title')}
        subTitle={confirmationModalItem?.name}
        text={t('mailTemplate.campaigns.deleteModal.text')}
        submitText={t('mailTemplate.campaigns.deleteModal.submitText')}
        cancelText={t('mailTemplate.campaigns.deleteModal.cancelText')}
        submitBtnColor='error'
      />
    </Box>
  )
}

export default CampaignCardList
